import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Trans } from 'react-i18next';

import Launcher from '@ringcentral/rcv-ui/src/components/Launcher';
import Button from '@ringcentral/rcv-ui/src/components/Button';
import Tooltip from '@ringcentral/rcv-ui/src/components/Tooltip';
import {
    OS,
    OS_INFO,
    isMobile,
    isIOS,
    isChrome,
    isWin,
    isSafari,
} from '@ringcentral/rwc-utils/src/client-detection';
import { withAnimatedBanner } from '@rwc/components/with-animated-banner-hoc';
import Footer from '@rwc/components/footer';
import { Logo } from '@rwc/components/logo';
import { Logo as MobileLogo } from '@rwc/components/logo.mobile';
import { SUCCESS } from '@ringcentral/rwc-utils/src/constants/severities';
import { copyToClipboard } from '@ringcentral/rwc-utils/src/copy-to-clipboard';
import SchemeTip from '@rwc/components/scheme-tip';
import { OutdatedBrowsersTips, UnsupportedJoinSteps } from '@rwc/components/unsupported';

import { gotoOnconf, meetingId, isDownloadE2ee } from '@low/guard';

import {
    downloadApplication,
    isWebAllowed,
    isCurrentClientOutdated,
    getSupportedBrowsersList,
    getClientDownloadLink,
    getCurrentClientDownloadLink,
    isCurrentClientSupported,
    canApplicationBeLaunched,
} from '@low';

import '@rwc/utils/desktop-tools/feature-flags/log';
import { makeOverridableFunction } from '@ringcentral/rwc-utils/src/override-api';
import { GET_MEETING_URL } from '@ringcentral/rwc-bridge';
import { trackUnSupportPage } from '@rwc/utils/mobile-data-track';
import MobileStatesListener from '@rwc/containers/mobile-states-listener';
import { secureAnalytics } from '@ringcentral/rwc-utils/src/analytics/secure-analytics';

const getMeetingUrl = makeOverridableFunction(
    id => `${location.origin}/join/${id}${location.search}`,
    GET_MEETING_URL
);

class Download extends PureComponent {
    state = {
        orientation: 'landscape',
    };

    isUnsupportedMobileBrowsers = isMobile && !isCurrentClientSupported();

    isUnsupportedAndroidChrome =
        OS_INFO === OS.ANDROID && isChrome() && !isCurrentClientSupported();

    isUnsupportedIOSSafari = isIOS && isSafari() && !isCurrentClientSupported();

    isOutdated = isCurrentClientOutdated();

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        secureAnalytics.track('RCV_Web_download_open', { action: 'Download page is open' });
        const shouldTrackUnsupportedBrowsersInfo = !isMobile && !isCurrentClientSupported();
        if (shouldTrackUnsupportedBrowsersInfo) {
            if (this.isOutdated) {
                secureAnalytics.track('RCV_Web_unsupportedBrowserVersion', { system: OS_INFO });
            } else {
                secureAnalytics.track('RCV_Web_unsupportedBrowser', { system: OS_INFO });
            }
        }
    }

    get meetingURL() {
        return getMeetingUrl(meetingId);
    }

    handleMobileOrientation = ({ orientation }) => {
        this.setState({ orientation });
    };

    copyMeetingURL = () => {
        this.trackCopyMeetingEvent();
        copyToClipboard(this.meetingURL).then(() => this.showCopyMeetingBanner());
    };

    copyOnEnter = e => {
        if (e.key === 'Enter') {
            this.copyMeetingURL();
        }
    };

    trackCopyMeetingEvent = () => {
        secureAnalytics.track('RCV_Web_meeting_link_copied', {
            action: '"Copy" meeting link button is clicked',
        });
    };

    showCopyMeetingBanner = () => {
        this.props.showBanner({
            message: i18n.t('banner.COPIED_TO_CLIPBOARD'),
            severity: SUCCESS,
        });
    };

    renderTip() {
        if (isCurrentClientOutdated()) {
            const file = getCurrentClientDownloadLink();

            return (
                <div>
                    <Trans
                        i18nKey='DOWNLOAD.UPDATE_TIP'
                        components={{
                            update: <a href={file} data-at='DownloadPage::Button::Update' />,
                        }}
                    />
                </div>
            );
        } else {
            /* eslint-disable jsx-a11y/anchor-is-valid */
            return (
                <div>
                    <Trans
                        i18nKey='DOWNLOAD.LAUNCH_TIP'
                        components={{
                            tooltip: (
                                <Tooltip
                                    placement='top'
                                    className='meeting-url'
                                    text={i18n.t('DOWNLOAD.COPY_TIP')}
                                />
                            ),
                            url: (
                                <a
                                    tabIndex='0'
                                    onKeyDown={this.copyOnEnter}
                                    data-at='DownloadPage::Button::CopyMeetingURL'
                                    onClick={this.copyMeetingURL}
                                />
                            ),
                            browser: (
                                <Fragment>
                                    {getSupportedBrowsersList().map(client => (
                                        <span key={client}>
                                            <a
                                                href={getClientDownloadLink(client)}
                                                data-at={`DownloadPage::Button::SupportedBrowserLink::${client}`}>
                                                {i18n.t(`BROWSERS.${client}`)}
                                            </a>{' '}
                                        </span>
                                    ))}
                                </Fragment>
                            ),
                        }}
                    />
                </div>
            );
            /* eslint-enable jsx-a11y/anchor-is-valid */
        }
    }

    downloadApplicationAndShowTip = () => {
        secureAnalytics.track('RCV_Web_download_link_clicked', {
            action: 'Download link is clicked',
        });
        downloadApplication(meetingId);
        if (isMobile && meetingId) copyToClipboard(this.meetingURL);

        if (this.isUnsupportedMobileBrowsers) trackUnSupportPage('Download RingCentral');
    };

    renderSimpleDownload() {
        return (
            <div>
                {this.renderTitleAndBtn()}
                {meetingId && isWebAllowed() ? this.renderTip() : null}
            </div>
        );
    }

    renderTitleAndBtn() {
        return (
            <Fragment>
                <h3 className='welcome-text'>{i18n.t('DOWNLOAD.GREETINGS_NO_NAME')}</h3>
                <Button
                    data-at='DownloadPage::Button::Download'
                    onClick={this.downloadApplicationAndShowTip}
                    variant='round contained'>
                    {i18n.t('DOWNLOAD.DOWNLOAD_BTN', {
                        applicationName: i18n.t('DOWNLOAD.APPLICATION'),
                    })}
                </Button>
            </Fragment>
        );
    }

    handleDownloadApplication = () => {
        this.downloadApplicationAndShowTip(meetingId);
    };

    renderWebJoin() {
        return (
            <Fragment>
                <h3 className='welcome-text'>{i18n.t('DOWNLOAD.GREETINGS_NO_NAME')}</h3>

                <div className='login-form'>
                    <div className='join-button-wrapper'>
                        <Button
                            onClick={gotoOnconf}
                            variant='round contained'
                            data-at='DownloadPage::Button::WebJoin'>
                            {i18n.t('DOWNLOAD.JOIN_FROM_WEB')}
                        </Button>
                    </div>
                </div>
            </Fragment>
        );
    }

    renderE2eeUnsupportedClientJoinSteps() {
        return (
            <Fragment>
                <div className='join-steps-wrapper'>
                    <h3 className='welcome-text'>
                        {i18n.t('DOWNLOAD.web_join_steps.to_join_the_e2ee_meeting')}
                    </h3>
                    <div className='step'>
                        <div className='step-order'>1</div>

                        <div className='step-content'>
                            <p>
                                {i18n.t('DOWNLOAD.web_join_steps.rejoin_using')}
                                <a href={getClientDownloadLink('chrome')}>
                                    {i18n.t('DOWNLOAD.THE_LATEST_VERSION_OF', {
                                        browser: i18n.t('BROWSERS.chrome'),
                                    })}
                                </a>
                                {isWin ? (
                                    <Fragment>
                                        &nbsp;{i18n.t('global.OR')}&nbsp;{' '}
                                        <a href={getClientDownloadLink('edge')}>
                                            {i18n.t('DOWNLOAD.THE_LATEST_VERSION_OF', {
                                                browser: i18n.t('BROWSERS.edge'),
                                            })}
                                        </a>
                                    </Fragment>
                                ) : null}
                            </p>
                        </div>
                    </div>
                    <div className='step'>
                        <div className='step-order'>2</div>

                        <div className='step-content'>
                            <div className='copy-link-wrapper'>
                                <div className='link'>
                                    <span>{this.meetingURL}</span>
                                </div>
                                <button
                                    onKeyDown={this.copyOnEnter}
                                    data-at='DownloadPage::Button::CopyMeetingURL'
                                    onClick={this.copyMeetingURL}
                                    type='button'
                                    className='copy-button'>
                                    {i18n.t('DOWNLOAD.COPY_LINK')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='step'>
                        <div className='step-order'>3</div>

                        <div className='step-content'>
                            <p>
                                <Trans i18nKey='DOWNLOAD.web_join_steps.paste_the_link_in_or_rejoin' />
                            </p>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    renderUnsupportedClientJoinSteps() {
        if (this.isOutdated) {
            return <OutdatedBrowsersTips onDownloadApplication={this.handleDownloadApplication} />;
        }

        return (
            <UnsupportedJoinSteps
                copyButtonDataAt='DownloadPage::Button::CopyMeetingURL'
                onSuccessCopyLink={this.showCopyMeetingBanner}
                trackCopyMeetingEvent={this.trackCopyMeetingEvent}
            />
        );
    }

    get supportedBrowsersList() {
        return getSupportedBrowsersList();
    }

    renderSimpleDownloadOrUnsupportedClientJoinSteps() {
        if (!meetingId) return this.renderSimpleDownload();

        return this.supportedBrowsersList.length > 0
            ? this.renderUnsupportedClientJoinSteps()
            : this.renderSimpleDownload();
    }

    renderUnsupportedMobileBrowsersTips() {
        const createUnsupportedTips = key => (
            <div className='launcher-unsupport-reason'>
                <p>{i18n.t('LAUNCHER.MOBILE_REASON')}</p>
                <p>
                    {i18n.t(key, {
                        applicationName: i18n.t('DOWNLOAD.APPLICATION'),
                    })}
                </p>
            </div>
        );

        /* eslint-disable-next-line no-nested-ternary */
        return this.isUnsupportedAndroidChrome
            ? createUnsupportedTips('LAUNCHER.LOWER_ANDROID_CHROME_JOIN_MESSAGE')
            : /* eslint-disable-next-line no-nested-ternary */
            this.isUnsupportedIOSSafari
            ? createUnsupportedTips('LAUNCHER.LOWER_IOS_SAFARI_JOIN_MESSAGE')
            : this.isUnsupportedMobileBrowsers
            ? createUnsupportedTips('LAUNCHER.MOBILE_JOIN_MESSAGE')
            : null;
    }

    renderMobileContent() {
        return (
            <div className='launcher-content'>
                <h3 className='welcome-text'>{i18n.t('DOWNLOAD.GREETINGS_NO_NAME')}</h3>
                {this.renderUnsupportedMobileBrowsersTips()}
                <Button
                    data-at='LauncherPage::Button::Download'
                    onClick={this.downloadApplicationAndShowTip}
                    variant='big'>
                    {i18n.t('DOWNLOAD.DOWNLOAD_BTN', {
                        applicationName: i18n.t('DOWNLOAD.APPLICATION'),
                    })}
                </Button>
                <SchemeTip
                    orientation='landscape'
                    track={this.isUnsupportedMobileBrowsers ? trackUnSupportPage : undefined}
                />
            </div>
        );
    }

    renderContent() {
        if (isMobile) {
            return this.renderMobileContent();
        }

        if (isDownloadE2ee) {
            return this.renderE2eeUnsupportedClientJoinSteps();
        } else {
            const isAllowedToJoinFromWeb = meetingId && isCurrentClientSupported();
            return isAllowedToJoinFromWeb
                ? this.renderWebJoin()
                : this.renderSimpleDownloadOrUnsupportedClientJoinSteps();
        }
    }

    getRendererComponents = () => {
        const { orientation } = this.state;
        const applicationName = i18n.t('DOWNLOAD.APPLICATION');
        const classPrefix = isMobile
            ? `mobile-browser mobile-orientation-${orientation}`
            : 'desktop-browser';
        const downloadLogo = !isMobile ? Logo : MobileLogo;
        const showDownloadElement =
            meetingId && this.supportedBrowsersList.length > 0 && !this.isOutdated;
        /* eslint-disable jsx-a11y/anchor-is-valid */
        const afterElement = !isMobile ? (
            <div className='trouble-helper'>
                {showDownloadElement && (
                    <p>
                        <Trans
                            i18nKey='DOWNLOAD.DOWNLOAD_TIP.MESSAGE'
                            components={{
                                download: (
                                    <a
                                        onClick={() =>
                                            this.downloadApplicationAndShowTip(meetingId)
                                        }
                                        data-at='DownloadPage::Button::DownloadTip'
                                    />
                                ),
                            }}
                            values={{ applicationName: applicationName }}
                        />
                    </p>
                )}
                {canApplicationBeLaunched() && <SchemeTip />}
            </div>
        ) : (
            <SchemeTip orientation='portrait' />
        );
        /* eslint-enable jsx-a11y/anchor-is-valid */
        const downloadFooter = !isMobile ? (
            <Footer />
        ) : (
            <div className={classPrefix}>
                <Footer />
            </div>
        );

        return [classPrefix, downloadLogo, afterElement, downloadFooter];
    };

    render() {
        const [classPrefix, downloadLogo, afterElement, downloadFooter] =
            this.getRendererComponents();

        return (
            <Fragment>
                <MobileStatesListener onChange={this.handleMobileOrientation} />
                <Launcher
                    className={classPrefix}
                    Logo={downloadLogo}
                    titleText={i18n.t('DOWNLOAD.TITLE')}
                    afterElement={afterElement}>
                    {this.renderContent()}
                </Launcher>
                {downloadFooter}
            </Fragment>
        );
    }
}

Download.propTypes = {
    showBanner: PropTypes.func.isRequired,
};

export default withAnimatedBanner(Download);
